
import React from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { SEOHead } from '@/components/SEOHead';
import { Star, ExternalLink, Crown, Target, Camera, ArrowRight } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { getRewardfulReferral } from '@/utils/rewardful';
import { redirectToCheckout } from '@/lib/analytics';

const About: React.FC = () => {
  const { user, isSubscribed } = useAuth();
  const navigate = useNavigate();

  const handlePremiumUpgrade = async () => {
    if (!user) {
      navigate('/register');
      return;
    }

    try {
      const { data, error } = await supabase.functions.invoke('create-checkout', {
        body: { referralId: getRewardfulReferral() }
      });

      if (error) {
        console.error('Error creating checkout:', error);
        return;
      }

      if (data?.url) {
        redirectToCheckout(data.url, 'Premium');
      }
    } catch (error) {
      console.error('Error creating checkout:', error);
    }
  };

  return (
    <>
      <SEOHead
        title="About Milky Way Planner — Built by an Astrophotographer"
        description="Milky Way Planner was built by astrophotographer Eric D. Brown. A free galactic core visibility calendar with moon phases and nightly ratings."
        keywords="about milky way planner, astrophotography planning tool, eric brown astrophotographer, night sky photography, milky way planner creator"
        canonicalUrl="/about"
      />
      <div className="min-h-screen bg-background">
        <section className="bg-slate-950 py-16 sm:py-20">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
            <span className="eyebrow text-gold-light mb-4 block">About</span>
            <h1 className="text-3xl md:text-4xl text-white tracking-[-0.03em] mb-4">
              About Milky Way Planner
            </h1>
            <p className="text-lg text-white/70 leading-relaxed max-w-2xl mx-auto">
              Built by an astrophotographer, for astrophotographers.
            </p>
          </div>
        </section>

        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-12">

        {/* Narrative with image */}
        <div className="grid md:grid-cols-5 gap-8 mb-12">
          <div className="md:col-span-3 space-y-4 leading-relaxed">
            <p className="text-muted-foreground">
              I spend more time planning astrophotography trips than actually shooting them. If you've tried to schedule a Milky Way session, you know how it goes. Open PhotoPills, scroll through each night one at a time, check moonset, twilight, galactic core position, then maybe start a spreadsheet to keep track of the good dates.
            </p>
            <p className="text-foreground font-medium">
              I got tired of that workflow. So I built Milky Way Planner.
            </p>
            <p className="text-muted-foreground">
              At its core, the tool calculates when the galactic center is above the horizon during astronomical darkness for any location on Earth. It combines that with moon phase data, sunrise/sunset times, and twilight windows so you can see the full picture for an entire month at a glance. Pick a location and a month, and instead of checking each night individually, you can scan the whole month and spot the best dates in a few seconds.
            </p>
            <p className="text-muted-foreground">
              The visibility rating (1 to 10) is based on how long the galactic core is visible in true darkness and how much the moon interferes. A new moon with a long dark window scores high. A full moon that's up all night scores low. Simple as that.
            </p>
            <div className="p-5 rounded-lg bg-yellow-50 border border-yellow-200">
              <p className="text-yellow-800">
                <span className="font-medium">Premium ($25/year)</span> adds a Moon Planner for lunar photography (supermoon tracking, rise/set directions, golden hour overlap), a Trip Planner for multi-stop itineraries, saved locations, and exports to Excel, CSV, and calendar formats. Everything I wanted when I was planning trips across the desert Southwest.
              </p>
            </div>
            <p className="text-muted-foreground">
              The tool doesn't tell you when to shoot, but it gives you everything you need to figure that out quickly. It saves me about an hour every time I plan a session. I hope it does the same for you.
            </p>
            <div className="pt-4">
              <p className="text-foreground font-semibold">
                Eric D. Brown, D.Sc.<br />
                <span className="text-primary">Photographer</span>
              </p>
              <p className="text-muted-foreground mt-2">
                <a href="https://imagesbyericbrown.com" target="_blank" rel="noopener noreferrer" className="text-primary hover:text-primary/80 underline inline-flex items-center gap-1">
                  Images by Eric D. Brown <ExternalLink className="w-4 h-4" />
                </a>
              </p>
            </div>
          </div>
          <div className="md:col-span-2">
            <img
              src="/lovable-uploads/429f23c9-b522-430c-8d9c-f8e47a1ef3eb.webp"
              alt="Milky Way photography by Eric D. Brown"
              className="rounded-xl shadow-lg w-full h-auto sticky top-24"
            />
          </div>
        </div>

        {/* CTA cards */}
        <div className="border-t border-border pt-8 mb-8 grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Link to="/planner" className="group p-6 rounded-xl bg-[#0f1923] hover:bg-[#1a2b3d] transition-colors">
            <div className="flex items-center gap-2 mb-2">
              <Target className="w-5 h-5 text-gold" />
              <h4 className="font-semibold text-white">Plan Your Shoot</h4>
            </div>
            <p className="text-sm text-white/60 leading-relaxed mb-3">
              Free Milky Way calendar with nightly visibility ratings.
            </p>
            <span className="text-sm font-semibold text-gold group-hover:text-gold-light transition-colors inline-flex items-center gap-1">
              Open Planner <ArrowRight className="w-3.5 h-3.5" />
            </span>
          </Link>
          <Link to="/workshops" className="group p-6 rounded-xl bg-[#0f1923] hover:bg-[#1a2b3d] transition-colors">
            <div className="flex items-center gap-2 mb-2">
              <Camera className="w-5 h-5 text-gold" />
              <h4 className="font-semibold text-white">Browse Workshops</h4>
            </div>
            <p className="text-sm text-white/60 leading-relaxed mb-3">
              Astrophotography workshops at dark sky locations.
            </p>
            <span className="text-sm font-semibold text-gold group-hover:text-gold-light transition-colors inline-flex items-center gap-1">
              Find a Workshop <ArrowRight className="w-3.5 h-3.5" />
            </span>
          </Link>
          {(!user || !isSubscribed) ? (
            <Link to="/register" className="group p-6 rounded-xl bg-gold/10 border border-gold/20 hover:bg-gold/15 transition-colors">
              <div className="flex items-center gap-2 mb-2">
                <Star className="w-5 h-5 text-gold" />
                <h4 className="font-semibold text-foreground">Go Premium</h4>
              </div>
              <p className="text-sm text-muted-foreground leading-relaxed mb-3">
                Moon Planner, Trip Planner, exports for $25/year.
              </p>
              <span className="text-sm font-semibold text-gold group-hover:text-gold-dark transition-colors inline-flex items-center gap-1">
                Start Premium <ArrowRight className="w-3.5 h-3.5" />
              </span>
            </Link>
          ) : (
            <Link to="/workshops/instructors" className="group p-6 rounded-xl bg-[#0f1923] hover:bg-[#1a2b3d] transition-colors">
              <div className="flex items-center gap-2 mb-2">
                <Star className="w-5 h-5 text-gold" />
                <h4 className="font-semibold text-white">Meet Instructors</h4>
              </div>
              <p className="text-sm text-white/60 leading-relaxed mb-3">
                Browse instructor profiles and upcoming workshops.
              </p>
              <span className="text-sm font-semibold text-gold group-hover:text-gold-light transition-colors inline-flex items-center gap-1">
                View Instructors <ArrowRight className="w-3.5 h-3.5" />
              </span>
            </Link>
          )}
        </div>
      </div>

      </div>
    </>
  );
};

export default About;
